import React from 'react';
import {createRoot} from 'react-dom/client';
import {Provider} from 'react-redux';
import {NavigationContainer} from '@react-navigation/native';

import RootScreen from './src/component/screen/Root';
import MobileInterceptModal from './src/component/web/MobileInterceptModal/MobileInterceptModal';
import {webLinking} from './src/config/web-navigation.config';
import {ApiErrorProvider} from './src/provider/ApiErrorProvider';
import ChatProvider from './src/provider/ChatProvider';
import ErrorBoundaryProvider from './src/provider/ErrorBoundaryProvider';
import LaunchSequenceProvider from './src/provider/LaunchSequenceProvider';
import LoginProvider from './src/provider/LoginProvider';
import {MaintenancePageProvider} from './src/provider/MaintenancePageProvider';
import NavigationProvider from './src/provider/NavigationProvider';
import {NetInfoProvider} from './src/provider/NetInfoProvider';
import {PopupProvider} from './src/provider/PopupProvider';
import ReduxNetworkProvider from './src/provider/ReduxNetworkProvider';
import SafeAreaProvider from './src/provider/SafeAreaProvider';
import StackProvider from './src/provider/StackProvider';
import ThemeProvider from './src/provider/ThemeProvider';
import TranslationProvider from './src/provider/TranslationProvider';
import {UpdateAlertProvider} from './src/provider/UpdateAlertProvider';
import {UpdateNotesProvider} from './src/provider/UpdateNotesProvider';
import {UserConsentProvider} from './src/provider/UserConsentProvider';
import WebSessionProvider from './src/provider/WebSessionProvider';
import store from './src/redux/store/create';
import {viewportManager} from './src/util/web/viewport.util';

// Initialize viewport (e.g., force desktop mode if previously selected)
viewportManager.init();

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <Provider store={store}>
    <WebSessionProvider>
      <ReduxNetworkProvider>
        <LaunchSequenceProvider>
          <TranslationProvider>
            <ThemeProvider>
              <PopupProvider>
                <ApiErrorProvider>
                  <NetInfoProvider>
                    <SafeAreaProvider>
                      <NavigationContainer
                        linking={webLinking}
                        documentTitle={{
                          formatter: () => {
                            return `Optavia Connect`;
                          },
                        }}>
                        <NavigationProvider>
                          <StackProvider>
                            <LoginProvider>
                              <MaintenancePageProvider>
                                <ChatProvider>
                                  <UpdateAlertProvider>
                                    <UpdateNotesProvider>
                                      <UserConsentProvider>
                                        <ErrorBoundaryProvider hasBack={false}>
                                          <RootScreen />
                                        </ErrorBoundaryProvider>
                                      </UserConsentProvider>
                                    </UpdateNotesProvider>
                                  </UpdateAlertProvider>
                                </ChatProvider>
                              </MaintenancePageProvider>
                            </LoginProvider>
                          </StackProvider>
                        </NavigationProvider>
                      </NavigationContainer>
                      <MobileInterceptModal />
                    </SafeAreaProvider>
                  </NetInfoProvider>
                </ApiErrorProvider>
              </PopupProvider>
            </ThemeProvider>
          </TranslationProvider>
        </LaunchSequenceProvider>
      </ReduxNetworkProvider>
    </WebSessionProvider>
  </Provider>,
);

// Web-specific styling utilities for React Native Web
import {Platform, StyleSheet} from 'react-native';

// Import font to ensure it's processed by Webpack
import icomoonFont from './src/asset/linked/font/icomoon.ttf';

/**
 * Creates web-enhanced StyleSheet objects that work with React Native Web
 * Adds web-specific properties like cursor, hover states, and focus indicators
 * while maintaining compatibility with mobile platforms
 */
export const createWebEnhancedStyles = styles => {
  // If not on web, return regular styles
  if (Platform.OS !== 'web') {
    return StyleSheet.create(styles);
  }

  const enhancedStyles = {};

  Object.keys(styles).forEach(key => {
    const style = styles[key];

    if (!style) {
      enhancedStyles[key] = style;
      return;
    }

    // Clone the original style
    enhancedStyles[key] = {...style};

    // Add web-specific enhancements based on component type
    // Interactive elements get cursor pointer
    if (
      key.toLowerCase().includes('button') ||
      key.toLowerCase().includes('pressable') ||
      key.toLowerCase().includes('touchable') ||
      key.toLowerCase().includes('link')
    ) {
      enhancedStyles[key] = {
        ...enhancedStyles[key],
        cursor: 'pointer',
      };
    }

    // Form inputs get better web styling
    if (
      key.toLowerCase().includes('input') ||
      key.toLowerCase().includes('textinput')
    ) {
      enhancedStyles[key] = {
        ...enhancedStyles[key],
        cursor: 'text',
        // Web-specific input enhancements will be handled by RNW
      };
    }

    // Add web-specific properties that RNW can handle
    // Note: RNW automatically handles hover/focus through Pressable component props
  });

  return StyleSheet.create(enhancedStyles);
};

/**
 * Hook for web-specific interactive states
 * Provides hover and focus state management for web components
 */
export const useWebInteractionState = () => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isPressed, setIsPressed] = React.useState(false);

  const interactionProps =
    Platform.OS === 'web'
      ? {
          onHoverIn: () => setIsHovered(true),
          onHoverOut: () => setIsHovered(false),
          onFocus: () => setIsFocused(true),
          onBlur: () => setIsFocused(false),
        }
      : {};

  return {
    isHovered,
    isFocused,
    isPressed,
    setIsPressed,
    interactionProps,
  };
};

// Minimal web-specific CSS injection for global enhancements
const injectWebEnhancements = () => {
  if (Platform.OS !== 'web') return;

  const styleElement = document.createElement('style');
  styleElement.id = 'rnw-web-enhancements';

  styleElement.textContent = `
      @font-face {
        font-family: 'icomoon';
        src: url(${icomoonFont}) format('truetype');
        font-weight: normal;
        font-style: normal;
        font-display: swap;
      }

      /* ROOT CAUSE FIXES - Preserve RNW flex system, fix only specific issues */

      /* Fix main app container height - ensure content fills viewport */
      #root > div {
        height: 100vh !important;
        min-height: 100vh !important;
      }

        
        /* Chrome, Edge, Safari */
::-webkit-scrollbar {
  width: 0;
  height: 0;
}

/* Firefox */
* {
  scrollbar-width: none;
}

/* IE / old Edge */
* {
  -ms-overflow-style: none;
}

@keyframes slideInRight {
  from {
    transform: translateX(50px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
  `;

  // Only inject if not already present
  if (!document.getElementById('rnw-web-enhancements')) {
    document.head.appendChild(styleElement);
  }

  return () => {
    const existing = document.getElementById('rnw-web-enhancements');
    if (existing) {
      existing.remove();
    }
  };
};

// Inject web enhancements
injectWebEnhancements();
